import {
    DialogTitle,
    TextField,
    Grid,
    FormControlLabel,
    Switch,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Checkbox,
    ListItemButton,
    ListItemText,
    List, Tab, Tabs, Divider, Alert, AlertTitle, IconButton, Chip,
} from "@mui/material";
import {Dialog2} from "../../../../misc/Dialog2";
import {formatCents, parseCents, useAsync2} from "nate-react-api-helpers";
import {OpeningType2, Product, ProductScript, ProductUpdateMeta, RoundType} from "../../../../api/Products";
import {prefixedName, SupplierCategory} from "../../../../api/SupplierCategories";
import {AutoComplete} from "../openings/AutoComplete";
import {api} from "../../../../api/API";
import React, {useContext, useEffect, useRef, useState} from "react";
import {useSyncedRef} from "../../../../misc/SyncedRef";
import {SmoothActionBar} from "../../../../misc/SmoothActionBar";
import {CategoryTree} from "./CategoryTree";
import {GetOrCreateManufacturer2} from "../hardware/library/GetOrCreateManufacturer2";
import {Company} from "../../../../api/Companies";
import {FileItemSelection, ProductCatalogLookup} from "./ProductCatalogLookup";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
    PrepMenuOptions,
    usePrepMenuValue
} from "./PrepMenuOptions";
import {useAlternative} from "../../quote/alternative/Alternative";
import {CategoryProvider} from "./CategoryProvider";
import {Dimension} from "./Dimension";
import {DimensionInput} from "./DimensionInput";
import {InventorySelector, InvSelectorChange} from "./InventorySelector";
import {ProductCodeHelp} from "./ProductCodeHelp";
import {ProductProjectUsage} from "./ProductProjectUsage";
import {ProductUpdateMetaReview} from "./ProductUpdateMetaReview";
import {useConfirm} from "../../../../misc/Confirm";
import CloseIcon from '@mui/icons-material/Close';
import {DiscontinuedReplaceWith} from "./DiscontinuedReplaceWith";
import {DiscontinuedProductProjectList} from "./DiscontinuedProductProjectList";
import {ProductScripting} from "./ProductScripting";
import {useBackgroundAction} from "../../manage/sov/UseBackgroundTask";
import {ProjectContext} from "../../ProjectProvider";
import {AlternativeContext} from "../../quote/alternative/AlternativeContext";
import {useCanReviewProducts} from "../../../settings/products/Review";
import {IgnoreEditsForReview} from "./preps/IgnoreEditsForReview";
import {ReassignInventoryForNewCodes} from "./preps/ReassignInventoryForNewCodes";

export type CategoryKind = "hardware" | "div-10" | "frame-anchor" | "component" | null;

export function CreateProduct(props: {
    edit?: Product;
    mode: CategoryKind;
    onDone(value: Product): void;
    onCancel(): void;
}) {
    const [key, setKey] = useState(0);
    const [updated, setUpdated] = useState<Product>();
    const [initialTab, setInitialTab] = useState<string | undefined>(undefined);
    const projectContext = useContext(ProjectContext);
    const quoteContext = useContext(AlternativeContext);

    return (
        <CategoryProvider mode={props.mode}>
            <CreateProductInner
                key={key.toString()}
                edit={updated || props.edit}
                mode={props.mode}
                initialTab={initialTab}
                onDone={(value, reload) => {
                    if(reload) {
                        setUpdated(value);
                        setInitialTab(reload.tab)
                        setKey(Date.now());
                    } else {
                        props.onDone(value);
                    }

                    // trigger project reload to capture product recalculation in shop drawings
                    if(projectContext.valid) {
                        projectContext.reload();
                    }

                    if(quoteContext.valid) {
                        quoteContext.reload();
                    }
                }}
                onCancel={props.onCancel}
            />
        </CategoryProvider>
    )
}

type ReloadMeta = {
    tab: string;
}

function CreateProductInner(props: {
    edit?: Product;
    mode: CategoryKind;
    initialTab?: string;
    onDone(value: Product, reload?: ReloadMeta): void;
    onCancel(): void;
}) {

    const altId = useAlternative();
    const [defaultHwGroup, setDefaultHwGroup] = useState<OpeningType2>(props.edit?.defaultHwGroup||"active");
    const [defaultHwGroupUserSelected, setDefaultHwGroupUserSelected] = useState(false);
    const [defaultQtyStr, setDefaultQtyStr] = useState((props.edit?.defaultQty || 1).toString());
    const [defaultSupplierCategory, setDefaultSupplierCategory] = useState<{id: number; name: string} | null>(
        props.edit?.defaultSupplierCategory
            ? {id: props.edit.defaultSupplierCategory, name: props.edit.defaultSupplierCategoryName}
            : null
    );
    const [updateSupplierForOthersInCategory, setUpdateSupplierForOthersInCategory] = useState(false);

    useEffect(() => {
        if(defaultHwGroup === "common") return;
        setDefaultQtyStr("1");
    }, [defaultHwGroup]);

    const [category, setCategory] = useState<number|null>(props.edit?.category || null);
    const [catalogs, setCatalogs] = useState<FileItemSelection[]>(props.edit?.catalogs.map(c => {
        return {
            id: c.catalog,
            name: c.name,
            pages: c.pages,
        }
    }) || []);
    const [productCode, setProductCode] = useState(props.edit?.productCode || "");
    const [series, setSeries] = useState(props.edit?.series || "");
    const [defaultPrice, setDefaultPrice] = useState(props.edit?.defaultCostCents ? formatCents(props.edit.defaultCostCents) : "")
    const [finish, setFinish] = useState(props.edit?.finish || "");
    const [name, setName] = useState(() => {
        if(props.mode === "div-10") return (props.edit?.name || "").replace(/^.*?: /, "") // remove "PREFIX:" for div10 products
        return (props.edit?.name || "");
    });
    const [finishName, setFinishName] = useState(props.edit?.finishName || "");
    const [keyed, setKeyed] = useState(props.edit?.keyed || false);
    const [manufacturer, setManufacturer] = useState<Partial<Company>|undefined>(props.edit ? {
        id: props.edit.manufacturer,
        name: props.edit.manufacturerName,
    } : undefined);

    const [partOfFrame, setPartOfFrame] = useState(props.edit?.partOfFrame || false);

    const finishNameForCode = useAsync2(async (input) => {
        if(!input.code) return null;
        return await api.products.getFinishNameForCode(input);
    }, {code: finish}, [finish]);
    const lookupFinishName = finishNameForCode.result?.finishName;

    useEffect(() => {
        if(!lookupFinishName) return;
        setFinishName(lookupFinishName);
    }, [lookupFinishName]);

    const [categoryName, setCategoryName] = useState<string | undefined>(props.edit?.categoryName);
    const prepValues = usePrepMenuValue({
        category: category,
        isCreate: !props.edit,
        edit: props.edit,
    });

    const hwPrep = prepValues.value;

    const hasDims = !!(props.edit && (props.edit.dimHeightFormula || props.edit.dimWidthFormula || props.edit.dimLengthFormula))
    const [needDimensions, setNeedDimensions] = useState(hasDims);

    const [unitSystem, setUnitSystem] = useState(props.edit?.unitSystem || "imperial")

    const [hingeHeightDim, setHingeHeightDim] = useState(props.edit?.dimHeight || "");
    const isHinge = categoryName?.endsWith("Hinge") || false;

    useEffect(() => {
        if(!isHinge) return;

        if(hwPrep.singleActiveDoorPrep.startsWith("5\"")) {
            setHingeHeightDim("5\"");
        } else if(hwPrep.singleActiveDoorPrep.startsWith("4.5\"")) {
            setHingeHeightDim("4.5\"");
        }

    }, [hwPrep, isHinge]);

    const defaultLength = props.edit?.dimLengthFormula || "";
    const [length, setLength] = useState(defaultLength);
    const defaultRoundLength = props.edit?.dimLengthRoundInterval || ""
    const [roundLength, setRoundLength] = useState(defaultRoundLength)
    const defaultRoundTypeLength = props.edit?.dimLengthRoundType || "none"
    const [roundTypeLength, setRoundTypeLength] = useState(defaultRoundTypeLength);

    const defaultWidth = props.edit?.dimWidthFormula || "";
    const [width, setWidth] = useState(defaultWidth);
    const defaultRoundWidth = props.edit?.dimWidthRoundInterval || "";
    const [roundWidth, setRoundWidth] = useState(defaultRoundWidth)
    const defaultRoundTypeWidth = props.edit?.dimWidthRoundType || "none";
    const [roundTypeWidth, setRoundTypeWidth] = useState(defaultRoundTypeWidth);

    const defaultHeight = props.edit?.dimHeightFormula || ""
    const [height, setHeight] = useState(defaultHeight);
    const defaultRoundHeight = props.edit?.dimHeightRoundInterval || ""
    const [roundHeight, setRoundHeight] = useState(defaultRoundHeight)
    const defaultRoundTypeHeight = props.edit?.dimHeightRoundType || "none";
    const [roundTypeHeight, setRoundTypeHeight] = useState(defaultRoundTypeHeight);

    const [discontinued, setDiscontinued] = useState(props.edit?.discontinued || false);
    const [discontinuedReplaceWith, setDiscontinuedReplaceWith] = useState(props.edit?.discontinuedReplaceWith || [])

    const [perLengthPurchaseIncrement, setPerLengthPurchaseIncrement] = useState<string>(props.edit?.perLengthPurchaseIncrement || "");
    const [perLengthInventoryIncrement, setPerLengthInventoryIncrement] = useState<string|null>(props.edit?.perLengthInventoryIncrement || null);
    const [scripts, setScripts] = useState<ProductScript[]|undefined>(props.edit?.setProductScripts || undefined);

    const [reviewStatus, setReviewStatus] = useState(props.edit?.reviewStatus || "pending");

    const autoDimensionEdited = (() => {
        if(length !== defaultLength) return true;
        if(roundLength !== defaultRoundLength) return true;
        if(roundTypeLength !== defaultRoundTypeLength) return true;

        if(width !== defaultWidth) return true;
        if(roundWidth !== defaultRoundWidth) return true;
        if(roundTypeWidth !== defaultRoundTypeWidth) return true;

        if(height !== defaultHeight) return true;
        if(roundHeight !== defaultRoundHeight) return true;
        if(roundTypeHeight !== defaultRoundTypeHeight) return true;

        return false;
    })()

    const onDoneRef = useSyncedRef(props.onDone);
    const [showUpdateMeta, setShowUpdateMeta] = useState<{product: Product, updateMeta: ProductUpdateMeta, reload: ReloadMeta | undefined}|null>(null);

    const reloadMetaRef = useRef<ReloadMeta|undefined>(undefined);

    const submit = useBackgroundAction({
        callback: (raw: {
            obj: Product
            reload: ReloadMeta | undefined;
        }) => {
            const input = raw.obj;
            reloadMetaRef.current = raw.reload;

            if (input.dimLength) Dimension.validate(input.dimLength);
            if (input.dimLengthRoundType && input.dimLengthRoundType !== "none") validateRound(input.dimLengthRoundType, input.dimLengthRoundInterval);

            if(input.dimWidth) Dimension.validate(input.dimWidth);
            if(input.dimWidthRoundType && input.dimWidthRoundType !== "none") validateRound(input.dimWidthRoundType, input.dimWidthRoundInterval)

            if(input.dimHeight) Dimension.validate(input.dimHeight);
            if(input.dimHeightRoundType && input.dimHeightRoundType !== "none") validateRound(input.dimHeightRoundType, input.dimHeightRoundInterval);

            return api.products.upsertWithTask(input);
        },
        onDone: (result: {product: Product, updateMeta: ProductUpdateMeta}) => {
            const reload = reloadMetaRef.current;
            reloadMetaRef.current = undefined;

            if(isBlankUpdateMeta(result.updateMeta)) {
                onDoneRef.current(result.product, reload);
                return;
            }

            setShowUpdateMeta(Object.assign(result, {
                reload: reload,
            }));
        }
    });

    const confirmCtx = useConfirm();
    const [tab, setTab] = useState<"general"|"dimensions"|"projects"|"sourcing"|"warehouse"|"scripting">((props.initialTab as any) || "general");
    const [stockingChanges, setStockingChanges] = useState(new Map<number, InvSelectorChange>());

    const [ignoreUpdatesForProjectReview, setIgnoreUpdatesForProjectReview] = useState(false);
    const [reassignInventoryToNewCodes, setReassignInventoryToNewCodes] = useState(false);

    const [showCatalog, setShowCatalog] = useState<FileItemSelection>();
    const [showAddCatalog, setShowAddCatalog] = useState(false);

    const canReview = useCanReviewProducts();

    if(showCatalog) {
        return <ProductCatalogLookup name={props.mode === "frame-anchor" ? "Anchor Detail" : "Cut Sheet"} onDone={async (v) => {
                if(!v) return;

                setCatalogs(old => old.map(o => o.id === showCatalog?.id ? v : o));
                setShowCatalog(undefined);
            }} value={showCatalog} manufacturer={manufacturer?.id} category={category || undefined} />
    }

    if(showAddCatalog) {
        return <ProductCatalogLookup name={props.mode === "frame-anchor" ? "Anchor Detail" : "Cut Sheet"} onDone={async (v) => {
            setShowAddCatalog(false);
            if(!v) return;
            setCatalogs(old => old.concat(v));
        }} value={null} manufacturer={manufacturer?.id} category={category || undefined} />
    }

    const canHaveDimensions = !(props.mode === "frame-anchor")
    const canCombineWithFrame = props.mode === "frame-anchor";

    const save = (reload?: ReloadMeta) => {
        let updateObj: any = {};

        if(props.edit) {
            Object.assign(updateObj, props.edit);
        }

        if(hwPrep.singleFramePrepCustomTemplate) hwPrep.singleFramePrep = `${productCode} Prep - See attached template`;
        if(hwPrep.pairFramePrepCustomTemplate) hwPrep.pairFramePrep = `${productCode} Prep - See attached template`;
        if(hwPrep.singleActiveDoorPrepCustomTemplate) hwPrep.singleActiveDoorPrep = `${productCode} Prep - See attached template`;
        if(hwPrep.pairActiveDoorPrepCustomTemplate) hwPrep.pairActiveDoorPrep = `${productCode} Prep - See attached template`;
        if(hwPrep.pairInactiveDoorPrepCustomTemplate) hwPrep.pairInactiveDoorPrep = `${productCode} Prep - See attached template`;
        if(hwPrep.pairBothActiveDoorPrepCustomTemplate) hwPrep.pairBothActiveDoorPrep = `${productCode} Prep - See attached template`;
        let dfPrice: number | null = null;
        if(defaultPrice) {
            const c = parseCents(defaultPrice)
            if(c > 0) {
                dfPrice = c;
            }
        }

        if(isHinge) {
            updateObj.dimHeight = hingeHeightDim;
        }

        debugger;

        Object.assign(updateObj, {
            name: name, // name only has affect on div10 products, see backend for naming overrides
            productCode: productCode,
            series: series,
            finish: finish,
            finishName: finishName,
            manufacturer: manufacturer?.id,
            category: category,
            partOfFrame: partOfFrame,
            defaultHwGroup: defaultHwGroup,
            keyed: keyed,
            catalogs: catalogs.map(c => ({
                name: c.name,
                catalog: c.id,
                pages: c.pages,
            })),

            unitSystem: unitSystem,

            defaultQty: fixNaN(parseInt(defaultQtyStr || "1"), 1),
            defaultSupplierCategory: defaultSupplierCategory?.id ?? null,

            hwPrepSingleFrame: hwPrep.singleFramePrep,
            hwPrepSingleFrameCustomTemplate: hwPrep.singleFramePrepCustomTemplate,
            hwPrepSingleActiveDoor: hwPrep.singleActiveDoorPrep,
            hwPrepSingleActiveDoorCustomTemplate: hwPrep.singleActiveDoorPrepCustomTemplate,
            hwPrepPairFrame: hwPrep.pairFramePrep,
            hwPrepPairFrameCustomTemplate: hwPrep.pairFramePrepCustomTemplate,
            hwPrepPairActiveDoor: hwPrep.pairActiveDoorPrep,
            hwPrepPairActiveDoorCustomTemplate: hwPrep.pairActiveDoorPrepCustomTemplate,
            hwPrepPairInactiveDoor: hwPrep.pairInactiveDoorPrep,
            hwPrepPairInactiveDoorCustomTemplate: hwPrep.pairInactiveDoorPrepCustomTemplate,
            hwPrepPairBothActiveDoor: hwPrep.pairBothActiveDoorPrep,
            hwPrepPairBothActiveDoorCustomTemplate: hwPrep.pairBothActiveDoorPrepCustomTemplate,

            perLengthInventoryIncrement,
            perLengthPurchaseIncrement,

            dimLengthFormula: length || null,
            dimLengthRoundInterval: roundLength || null,
            dimLengthRoundType: roundTypeLength || null,

            dimWidthFormula: width || null,
            dimWidthRoundInterval: roundWidth || null,
            dimWidthRoundType: roundTypeWidth || null,

            dimHeightFormula: height || null,
            dimHeightRoundInterval: roundHeight || null,
            dimHeightRoundType: roundTypeHeight || null,

            defaultCostCents: dfPrice,

            discontinued: discontinued,
            discontinuedReplaceWith: discontinuedReplaceWith.map(d => d.id),
        })

        if(canReview) {
           updateObj.reviewStatus = reviewStatus;
        }

        if(scripts) {
            updateObj.setProductScripts = scripts;
        }

        if(props.edit && dfPrice !== props.edit.defaultCostCents && !!altId) {
            updateObj.updateQuotePricingForAlternative = altId;
        }

        if(ignoreUpdatesForProjectReview) {
            updateObj.ignoreUpdatesForProjectReview = true;
        }

        if(reassignInventoryToNewCodes) {
            updateObj.reassignInventoryToNewCodes = true;
        }

        if(updateSupplierForOthersInCategory) {
            updateObj.updateDefaultSupplierForOtherProductsInCategory = true;
        }

        if(props.edit) {
            updateObj.stockInventoryChanges = Object.fromEntries(stockingChanges);
        }

        submit.callback({
            obj: updateObj,
            reload: reload,
        })
    }

    if(showUpdateMeta) {
        return <ProductUpdateMetaReview product={showUpdateMeta.product} value={showUpdateMeta.updateMeta} onCancel={() => {
            setShowUpdateMeta(null);
        }} onDone={() => {
            setShowUpdateMeta(null);

            if(!showUpdateMeta) return;
            onDoneRef.current(showUpdateMeta.product, showUpdateMeta.reload);
        }} />
    }

    // @ts-ignore
    const lastUnitCostCents = props.edit ? formatCents(props.edit.lastUnitCostCents) : "-";

    return (
        <Dialog2 open={true} onClose={props.onCancel} maxWidth="xl">
            <DialogTitle>{props.edit ? "Edit Product" : "Create Product"}</DialogTitle>
            <Tabs onChange={(e, value) => setTab(value)} value={tab}>
                <Tab label="General" value="general" />
                {canHaveDimensions && <Tab label="Dimensions" value="dimensions" />}
                <Tab label="Sourcing" value="sourcing" />
                <Tab label="Warehouse" value="warehouse" />
                <Tab label="Projects" value="projects" />
                <Tab label="Scripting" value="scripting" />
            </Tabs>
            <Divider />
            <div style={{
                flex: 1, display: "flex", flexDirection: "column",
                padding: 24,
                overflow: "auto",
            }}>
                {tab === "general" ? <>
                    <div style={{
                        flex: 1, display: "flex", flexDirection: "row", alignItems: "stretch",
                        justifyContent: "flex-start", flexWrap: "nowrap",
                        overflow: "hidden",
                        maxWidth: 800,
                    }}>
                        <div style={{display: "flex", flexDirection: "column", overflowY: "auto"}}>
                            <CategoryTree
                                mode={props.mode}
                                value={category}
                                onCategoryName={setCategoryName}
                                onChange={(cat, info) => {
                                    if(cat === category) return;

                                    console.log("category", cat);
                                    setCategory(cat)

                                    if(defaultHwGroupUserSelected) return;
                                    if(info?.isAutoQtyHinge) {
                                        setDefaultHwGroup("each-door");
                                    } else if (cat === props.edit?.category) {
                                        setDefaultHwGroup(props.edit?.defaultHwGroup || "active");
                                    } else if(info) {
                                        setDefaultHwGroup("active");
                                    }
                                }}
                            />
                        </div>
                        <div style={{flex: 1, overflowY: "auto"}}>
                            <div style={{padding: 16, paddingBottom: 0}}>
                                {props.edit?.discontinued && <Alert severity="warning" style={{marginBottom: 20}}>
                                    <AlertTitle>Product Discontinued</AlertTitle>
                                    {props.edit.discontinuedReplaceWith && <>
                                        Use one of the following instead:
                                        <ul>
                                            {props.edit.discontinuedReplaceWith.map(d => <li>
                                                {d.productCode} ({d.finish}) from {d.manufacturerName}
                                            </li>)}
                                        </ul>
                                    </>}

                                    The following projects still reference this product
                                    <DiscontinuedProductProjectList product={props.edit.id} />

                                </Alert>}

                                {props.edit && <Alert severity="warning" style={{marginBottom: 20}}>
                                    Warning: This product may be used in other projects and will be updated there
                                    too.
                                </Alert>}

                                <GetOrCreateManufacturer2
                                    fullWidth
                                    size="small"
                                    label="Manufacturer"
                                    horizontal
                                    kind={props.mode === null ? null : props.mode === "div-10" ? "div10" : "hardware"}
                                    onChange={setManufacturer}
                                    value={manufacturer?.id}
                                />
                                <div style={{height: 16}}/>

                                <Grid container spacing={1}>

                                    {props.mode === "frame-anchor" ? <>
                                        <Grid item xs={12}>
                                            <FormControl fullWidth>
                                                <InputLabel id="description">Description</InputLabel>

                                                <Select labelId="description" size="small" fullWidth
                                                        label="Description" value={fixAnchorName(name) || "-"}
                                                        onChange={e => {
                                                            const opt = anchorLookupOptions().find(v => v.value === e.target.value as any)

                                                            if (opt) {
                                                                setName(opt.value)
                                                            } else {
                                                                setName(e.target.value as any)
                                                            }
                                                        }}>
                                                    <MenuItem value="-">Choose a Type</MenuItem>
                                                    {anchorLookupOptions().map(v => (
                                                        <MenuItem value={v.value}>{v.display}</MenuItem>))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField size="small" label="Product Code" value={productCode}
                                                       onChange={e => setProductCode(e.target.value)}/>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <ListItemButton
                                                onClick={() => {
                                                    if(catalogs.length === 0) {
                                                        setShowAddCatalog(true);
                                                    } else {
                                                        setShowCatalog(catalogs[0]);
                                                    }
                                                }}>
                                                <ListItemText
                                                    style={{flex: 1}}
                                                    secondary={catalogs[0]?.name || "None"}
                                                    primary="Anchor Detail"
                                                />
                                                <ChevronRightIcon/>
                                            </ListItemButton>
                                        </Grid>
                                    </> : <>
                                        {categoryName === "Component" && <Grid item xs={12}>
                                            <TextField size="small" label="Name" value={name} fullWidth
                                                       onChange={e => setName(e.target.value)}/>
                                        </Grid>}
                                        <Grid item xs={12} style={{
                                            display: "flex",
                                            flexDirection: "row",
                                            gap: 10,
                                        }}>
                                            <TextField size="small" label="Product Code" value={productCode}
                                                       onChange={e => setProductCode(e.target.value)}
                                                       fullWidth
                                                       style={{maxWidth: 350}}
                                                       InputProps={{
                                                            endAdornment: <ProductCodeHelp />
                                                       }}
                                            />
                                            <TextField size="small" label="Series" value={series} onChange={e => setSeries(e.target.value)} />
                                        </Grid>
                                        <Grid item xs={12} />
                                        <Grid item xs={3}>
                                            <TextField size="small" label="Finish" value={finish}
                                                       onChange={e => setFinish(e.target.value)}/>
                                        </Grid>
                                        <Grid item xs={3}>
                                            <TextField size="small" label="F. Desc" value={finishName}
                                                       onChange={e => setFinishName(e.target.value)}/>
                                        </Grid>
                                    </>}
                                    {props.mode === "div-10" && <>
                                        <Grid item xs={12}></Grid>
                                        <Grid item xs={12}>
                                            <TextField size="small"
                                                       label="Description"
                                                       value={name}
                                                       onChange={e => setName(e.target.value)}
                                                       fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <List dense>
                                                <ListItemButton
                                                    onClick={() => {
                                                        if(catalogs.length === 0) {
                                                            setShowAddCatalog(true);
                                                        } else {
                                                            setShowCatalog(catalogs[0]);
                                                        }
                                                    }}>
                                                    <ListItemText
                                                        style={{flex: 1}}
                                                        secondary={catalogs[0]?.name || "None"}
                                                        primary="Cut Sheet"
                                                    />
                                                    <ChevronRightIcon/>
                                                </ListItemButton>
                                            </List>
                                        </Grid>
                                    </>}
                                    {props.mode === "hardware" && <>
                                        <Grid item xs={12}></Grid>
                                        <Grid item xs={6}>
                                            <FormControl fullWidth>
                                                <InputLabel id="default-hw-group">Default HW Group</InputLabel>

                                                <Select labelId="default-hw-group" size="small" fullWidth
                                                        label="Default HW Group" value={defaultHwGroup}
                                                        onChange={e => {
                                                            setDefaultHwGroupUserSelected(true);
                                                            setDefaultHwGroup(e.target.value as any)
                                                        }}>
                                                    <MenuItem value="active">Active</MenuItem>
                                                    <MenuItem value="inactive">In-Active</MenuItem>
                                                    <MenuItem value="each-door">Each Door</MenuItem>
                                                    <MenuItem value="common">Common</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <FormControlLabel control={<Checkbox checked={keyed}
                                                                                 onChange={(e, checked) => setKeyed(checked)}/>}
                                                              label="Keyed"/>
                                        </Grid>
                                        {defaultHwGroup === "common" && <>
                                            <Grid item xs={6}>
                                                <TextField size="small" label="Default Qty"
                                                           value={defaultQtyStr}
                                                           onChange={e => setDefaultQtyStr(e.target.value)}/>
                                            </Grid>
                                            <Grid item xs={6}/>
                                        </>}
                                        <Grid item xs={12}></Grid>
                                        <Grid item xs={12}>
                                            <PrepMenuOptions
                                                onChange={prepValues.setValue}
                                                value={hwPrep}
                                                isCreate={!props.edit}
                                                category={category}
                                                group={prepValues.group}
                                                categories={prepValues.categories}
                                            />
                                        </Grid>
                                        {isHinge && <>
                                            <Grid item xs={12}></Grid>
                                            <Grid item xs={12}>
                                                <TextField label="Hinge Height" size="small" value={hingeHeightDim}
                                                           onChange={e => setHingeHeightDim(e.target.value)}
                                                           style={{width: 200}}/>
                                            </Grid>
                                        </>}
                                        <Grid item xs={12}></Grid>
                                        <Grid item xs={12}>
                                            <List dense>
                                                {catalogs.map(c =>
                                                    <ListItemButton
                                                        key={c.id}
                                                        onClick={() => setShowCatalog(c)}>

                                                        <ListItemText
                                                            style={{flex: 1}}
                                                            secondary={c.name}
                                                            primary="Cut Sheet"
                                                        />

                                                        <IconButton onClick={async (e) => {
                                                            e.stopPropagation();
                                                            const ok = await confirmCtx.confirm("Are you sure you want to remove this cutsheet?")
                                                            if(!ok) return;

                                                            setCatalogs(old => old.filter(o => o.id !== c.id));
                                                        }}>
                                                            <CloseIcon />
                                                        </IconButton>

                                                        <ChevronRightIcon/>
                                                    </ListItemButton>
                                                )}
                                                <ListItemButton
                                                    key="add"
                                                    onClick={() => setShowAddCatalog(true)}
                                                >
                                                    <ListItemText
                                                        style={{flex: 1}}
                                                        primary={catalogs.length === 0 ? "Cut Sheet" : "Add another Cut Sheet"}
                                                    />
                                                    <ChevronRightIcon/>
                                                </ListItemButton>
                                            </List>
                                        </Grid>
                                    </>}
                                </Grid>

                                {canCombineWithFrame && <>
                                    <div style={{height: 20}}/>
                                    <FormControlLabel control={<Switch checked={partOfFrame}
                                                                       onChange={(e, checked) => setPartOfFrame(checked)}/>}
                                                      label="Part of Frame"/>
                                </>}
                                {!needDimensions && <>
                                    <div style={{height: 16}}/>
                                    <div style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        gap: 16
                                    }}>
                                        <TextField label="Default Price" size="small" value={defaultPrice}
                                                   onChange={e => setDefaultPrice(e.target.value)}/>

                                        <TextField label="Last Purchase Price" size="small"
                                                   value={lastUnitCostCents}
                                                   disabled />

                                    </div>
                                </>}

                                <div style={{height: 16}}/>
                                <FormControlLabel control={<Switch checked={discontinued}
                                                                   onChange={(e, checked) => setDiscontinued(checked)}/>}
                                                  label="Discontinued"/>

                                {discontinued && <>
                                    <DiscontinuedReplaceWith
                                        id={props.edit?.id || 0}
                                        value={discontinuedReplaceWith}
                                        onChange={setDiscontinuedReplaceWith} />
                                </>}
                            </div>
                        </div>
                    </div>
                </> : tab === "dimensions" ? <>

                    <FormControlLabel control={<Switch checked={needDimensions}
                                                       onChange={(e, checked) => setNeedDimensions(checked)}/>}
                                      label="Has Dimensions"/>

                    {needDimensions && <>
                        <div style={{height: 16}}/>
                        <FormControl fullWidth>
                            <InputLabel id="unitSystem">Unit System</InputLabel>
                            <Select labelId="unitSystem" size="small" fullWidth label="Unit System"
                                    value={unitSystem} onChange={e => setUnitSystem(e.target.value as any)}>
                                <MenuItem value="imperial">Imperial</MenuItem>
                                <MenuItem value="metric">Metric</MenuItem>
                            </Select>
                        </FormControl>
                        <div style={{height: 16}}/>
                        {props.mode === "div-10" ? <>
                            <FormControlLabel control={
                                <Switch checked={length !== ""}
                                        onChange={(e, checked) => setLength(checked ? "0.1234\"" : "")}/>
                            } label="Length"/>

                            <div style={{height: 16}}/>
                            <FormControlLabel control={
                                <Switch checked={width !== ""}
                                        onChange={(e, checked) => setWidth(checked ? "0.1234\"" : "")}/>
                            } label="Width"/>

                            <div style={{height: 16}}/>
                            <FormControlLabel control={
                                <Switch checked={height !== ""}
                                        onChange={(e, checked) => setHeight(checked ? "0.1234\"" : "")}/>
                            } label="Height"/>
                        </> : <>
                            <DimensionInput
                                name="Length"
                                value={length}
                                onChange={setLength}
                                roundType={roundTypeLength}
                                onRoundTypeChange={setRoundTypeLength}
                                roundBy={roundLength}
                                onRoundByChange={setRoundLength}
                            />
                            <div style={{height: 16}}/>
                            <DimensionInput
                                name="Width"
                                value={width}
                                onChange={setWidth}
                                roundType={roundTypeWidth}
                                onRoundTypeChange={setRoundTypeWidth}
                                roundBy={roundWidth}
                                onRoundByChange={setRoundWidth}
                            />
                            <div style={{height: 16}}/>
                            <DimensionInput
                                name="Height"
                                value={height}
                                onChange={setHeight}
                                roundType={roundTypeHeight}
                                onRoundTypeChange={setRoundTypeHeight}
                                roundBy={roundHeight}
                                onRoundByChange={setRoundHeight}
                            />
                        </>}
                        <div style={{height: 16}}/>
                        <Alert style={{maxWidth: 450}}>
                            <AlertTitle>How it works</AlertTitle>
                            <div style={{whiteSpace: "normal"}}>
                                This creates rules so the software can automatically calculate dimensions for you.
                                This works just like Excel formulas.
                            </div>
                            <div style={{height: 16}}/>
                            <div>
                                <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                        Seal example:<br/>
                                        Length: door.width + door.height * 2 + 5”
                                    </Grid>
                                    <Grid item xs={6}>
                                        Threshold example:<br/>
                                        Width: frame.width - 3”<br/>
                                    </Grid>
                                </Grid>
                            </div>
                        </Alert>
                    </>}

                    <div style={{height: 16}}/>
                    <FormControlLabel control={<Switch checked={perLengthInventoryIncrement !== null}
                                                       onChange={(e, checked) => {
                                                           setPerLengthInventoryIncrement(checked ? "1'0" : null)
                                                           if (!checked) {
                                                               setPerLengthPurchaseIncrement("");
                                                           }
                                                       }}/>}
                                      label="Tracked Per Foot"/>

                    {perLengthInventoryIncrement !== null ? <>
                        <div style={{height: 16}}/>
                        <TextField size="small" label="Length of Purchase Item"
                                   value={perLengthPurchaseIncrement || ""}
                                   onChange={e => setPerLengthPurchaseIncrement(e.target.value)}/>
                        <div style={{height: 16}}/>
                        <Alert style={{maxWidth: 450}}>
                            <AlertTitle>How it works</AlertTitle>
                            <div>
                                This will track inventory per foot and allow you to purchase in a specific length
                                (e.g. 10 footers).
                            </div>
                        </Alert>
                    </> : null}

                </> : tab === "sourcing" ? <>

                    <AutoComplete<SupplierCategory>
                        label="Default Supplier Category"
                        size="small"
                        fullWidth
                        style={{maxWidth: 450}}
                        value={defaultSupplierCategory}
                        equals={(a, b) => a.id === b.id}
                        makeLabel={a => prefixedName(a)}
                        search={async search => {
                            const term = search.toLowerCase();
                            const list = await api.supplierCategories.listDefaults();
                            return list.filter(c => !c.archived && prefixedName(c).toLowerCase().includes(term));
                        }}
                        onSelect={v => setDefaultSupplierCategory({id: v.id, name: v.name})}
                    />
                    <div style={{height: 16}}/>
                    <FormControlLabel
                        disabled={!category}
                        control={<Checkbox checked={updateSupplierForOthersInCategory}
                                           onChange={(e, checked) => setUpdateSupplierForOthersInCategory(checked)}/>}
                        label={<span style={{fontSize: "0.8125rem"}}>{`Update all other products in ${categoryName || "this category"} (and sub-categories) to this default supplier category`}</span>}
                    />
                    {updateSupplierForOthersInCategory && <Alert severity="warning" style={{maxWidth: 450}}>
                        Quotes using these products will need to have their hardware bulk-edited for changes to apply to that quote. <br /><br />Use "Quote Hardware &gt; Edit By Product &gt; Supplier" to update a project.
                    </Alert>}
                </> : tab === "warehouse" ? <>
                    <InventorySelector
                        product={props.edit?.id}
                        autoDimensionEdited={autoDimensionEdited}
                        onChange={setStockingChanges}
                        onSave={() => save({
                                tab: "warehouse"
                        })}
                    />
                </> : tab === "projects" ? <>
                    <ProductProjectUsage product={props.edit?.id} />
                </> : tab === "scripting" ? <>
                    <ProductScripting
                        product={props.edit?.id}
                        value={scripts}
                        onChange={setScripts}
                    />
                </> : null}

            </div>
            <SmoothActionBar
                loading={submit.loading}
                error={submit.error}
                onCancel={props.onCancel}
                onSubmit={() => save()}
                actionName={props.edit ? "Save" : "Add"}
                submitDisabled={
                    props.mode === "frame-anchor" ?
                        (!category || !manufacturer || !productCode) :
                        (!category || !manufacturer || !finish || !productCode)
                }
                leftOfButtons={
                    <div style={{display: "flex", flexDirection: "column", alignItems: "flex-start"}}>
                        <IgnoreEditsForReview value={ignoreUpdatesForProjectReview} onChange={setIgnoreUpdatesForProjectReview} />
                        {props.edit && !(scripts?.length) &&
                            <ReassignInventoryForNewCodes value={reassignInventoryToNewCodes} onChange={setReassignInventoryToNewCodes} />}
                    </div>
                }
            >
                <div style={{display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4}}>
                    <Chip
                        size="small"
                        variant="outlined"
                        color={reviewStatus === "complete" ? "success" : "error"}
                        label={reviewStatus === "complete" ? "Approved" : "Product Needs Review"}
                    />
                    {canReview &&
                        <FormControlLabel
                            sx={{ml: 0}}
                            control={<Switch size="small" checked={reviewStatus === "complete"} onChange={(_, checked) => {
                                setReviewStatus(checked ? "complete" : "pending")
                            }} />}
                            label="Approve"
                            componentsProps={{typography: {variant: "body2"}}}
                        />}
                </div>
            </SmoothActionBar>
        </Dialog2>
    )
}

function isBlankUpdateMeta(meta: ProductUpdateMeta) {
    return Object.values(meta).every(v => !v || v.length === 0)
}

function fixAnchorName(name: string) {
    const found = anchorLookupOptions().find(c => c.value === name);
    if(!found) return ""
    return found.value;
}

export function anchorLookupOptions() {
    return [
        {value: "AWA", display: "AWA: Adjustable Wall Anchor"},
        {value: "BASE", display: "BASE: Base/Floor Anchor"},
        {value: "EMA", display: "EMA: Existing Masonry Anchor"},
        {value: "FSA", display: "FSA: 5 3/4\" Fixed Stud Anchor"},
        {value: "IMP", display: "IMP: Insulated Metal Panel Anchor"},
        {value: "P&S", display: "P&S: Plug & Strap"},
        {value: "S&T", display: "S&T: Strap & Tension"},
        {value: "WIRE", display: "WIRE: Masonry Wire Anchor"},
        {value: "Z", display: "Z: Welded \"Z\" Anchor"},
    ]
}

function fixNaN(value: any, defaultValue: number) {
    if(isNaN(value)) {
        return defaultValue
    }

    return value;
}

function validateRound(type: RoundType, length?: string | null) {
    if(type === "round-up-by-fixed-interval") {
        if(!length) throw new Error("Missing rounding interval");
        Dimension.validate(length)
        return;
    }

    if(type === "round-up-by-list-interval") {
        if(!length) throw new Error("Missing rounding list");
        length.split(",").map(i => Dimension.validate(i))
        return;
    }

    return
}